import { NetworkStatus, useQuery } from '@apollo/client';

import { SearchMode } from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { CITY, ROLE, SKILL } from '@xing-com/crate-jobs-domain-serps-constants';
import { getJobSerpsSearchQueryVariables } from '@xing-com/crate-jobs-domain-serps-helpers';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';
import { JobSearchByQueryDocument } from '@xing-com/crate-jobs-graphql';
import type { JobSearchByQueryQueryVariables } from '@xing-com/crate-jobs-graphql';
import { useSearchState } from '@xing-com/crate-jobs-hooks';

import { isVisibleJob } from '../../../helpers';
import type { JobItemResultVisibleJob } from '../../../helpers';
import { useSearchConsumer } from '../../../hooks/use-search-consumer';

const NEARBY_RESULTS_RADIUS = 50;
const NEARBY_RESULTS_PAGE_SIZE = 10;

type Args = {
  serps: SerpsRouteParams | null;
  mainResults: JobItemResultVisibleJob[];
  primaryJobCount: number;
};

type Result = {
  jobs: JobItemResultVisibleJob[];
  total: number;
  fetchedCount: number;
  isLoading: boolean;
  isFetchingMore: boolean;
  shouldFetch: boolean;
  fetchNextPage: () => Promise<void>;
};

const isSupportedSerp = (serps: SerpsRouteParams | null): boolean => {
  if (!serps) {
    return false;
  }

  return (
    serps.category === CITY ||
    (serps.category === ROLE && serps.subcategory === CITY) ||
    (serps.category === SKILL && serps.subcategory === CITY)
  );
};

const getJobIdentifier = (job: JobItemResultVisibleJob): string =>
  job.jobDetail.globalId || job.jobDetail.id;

export const useSerpNearbyResults = ({
  serps,
  mainResults,
  primaryJobCount,
}: Args): Result => {
  const { isLoggedOut } = useLoginState();
  const { searchQuery, sort, sincePeriod, since } = useSearchState();
  const consumer = useSearchConsumer();

  const serpsSearchQuery = serps
    ? getJobSerpsSearchQueryVariables(serps)
    : undefined;

  const finalKeywords = searchQuery.keywords || serpsSearchQuery?.keywords;
  const finalFilter = searchQuery.filter || serpsSearchQuery?.filter;
  const finalLocation = searchQuery.location || serpsSearchQuery?.location;
  const normalizedSince =
    since && since.slice(-1) !== 'Z' ? `${since}Z` : since;

  const shouldFetch =
    isLoggedOut &&
    isSupportedSerp(serps) &&
    primaryJobCount <= 5 &&
    Boolean(finalLocation);

  const initialLimit =
    NEARBY_RESULTS_PAGE_SIZE + Math.min(mainResults.length, primaryJobCount, 5);

  const variables: JobSearchByQueryQueryVariables | undefined =
    shouldFetch && finalLocation
      ? {
          query: {
            ...searchQuery,
            ...(finalKeywords && { keywords: finalKeywords }),
            ...(finalFilter && { filter: finalFilter }),
            location: {
              ...finalLocation,
              radius: NEARBY_RESULTS_RADIUS,
            },
          },
          consumer,
          sort,
          limit: initialLimit,
          offset: 0,
          sincePeriod,
          since: normalizedSince,
          trackRecent: false,
          searchMode: SearchMode.Semantic,
          boundedTotals: true,
          returnAggregations: false,
        }
      : undefined;
  const { data, fetchMore, loading, networkStatus } = useQuery(
    JobSearchByQueryDocument,
    {
      skip: !variables,
      variables,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const total = data?.jobSearchByQuery?.total ?? 0;
  const fetchedCount = data?.jobSearchByQuery?.collection?.length ?? 0;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const jobs = (data?.jobSearchByQuery?.collection ?? [])
    .filter(isVisibleJob)
    .filter((item) => {
      const itemId = getJobIdentifier(item);
      return !mainResults.some(
        (mainResult) => getJobIdentifier(mainResult) === itemId
      );
    });

  const fetchNextPage = async (): Promise<void> => {
    if (!shouldFetch || fetchedCount >= total || isFetchingMore) {
      return;
    }

    await fetchMore({
      variables: {
        offset: fetchedCount,
        limit: NEARBY_RESULTS_PAGE_SIZE,
        returnAggregations: false,
      },
      // @ts-expect-error generated and fetchMore types are incompatible because of undefined cases
      updateQuery: (previousResult, { fetchMoreResult }) => ({
        ...previousResult,
        jobSearchByQuery: {
          ...(previousResult?.jobSearchByQuery ?? {}),
          collection: [
            ...(previousResult?.jobSearchByQuery?.collection ?? []),
            ...(fetchMoreResult?.jobSearchByQuery?.collection ?? []),
          ],
        },
      }),
    });
  };

  return {
    jobs,
    total,
    fetchedCount,
    isLoading: loading && networkStatus !== NetworkStatus.fetchMore,
    isFetchingMore,
    shouldFetch,
    fetchNextPage,
  };
};
